import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { LinearProgress } from "@mui/material";

import Sidebar from "../Components/Sidebars/main";
import NavBar from "../Components/Navbars/main";
import ActionBarComponent from "../Components/common/ActionBarComponent";
import Footer from "../Components/common/footer";
import { CaseNavigatorModalProvider } from "../Components/CaseNavigatorDashboard/CaseNavigatorModalProvider";
import useCaseTypes from "../Components/CaseNavigatorDashboard/NewCaseNavigatorPage/hooks/useCaseType";
import NewTabs from "../Components/new-client-dashboard/NewTabs";
import FilterIcon from "../assets/filter-icon-color.svg";
import StageStatusFilterModal from "../Components/CaseNavigatorDashboard/NewCaseNavigatorPage/components/StageStatusFilterModal";
import { fetchPageObject, fetchShakespeareStatus, getCaseId } from "../Utils/helper";
import CaseAgeTab from "../Components/CaseNavigatorDashboard/NewCaseNavigatorPage/components/case-age-tab";
import StateTab from "../Components/CaseNavigatorDashboard/NewCaseNavigatorPage/components/state-tab";
import CountyTab from "../Components/CaseNavigatorDashboard/NewCaseNavigatorPage/components/county-tab";
import InjuryBodyPartTab from "../Components/CaseNavigatorDashboard/NewCaseNavigatorPage/components/injury-tab";
import CaseNavigatorIcon from "../assets/case-navigator-logo.svg";
import NewChangesTabs from "../Components/new-client-dashboard/NewChangesTabs";

const NewCaseNavigatorPage = () => {
  const dispatch = useDispatch();
  const caseId = getCaseId();
  const [currentTab, setCurrentTab] = useState("caseage");

  const handleFilterChange = useCallback((filter) => {
    setCurrentTab(filter);
  }, []);

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [currentFilter, setCurrentFilter] = useState(null);
  const [caseTypesWithCases, setCaseTypesWithCases] = useState(null);
  const [pageObject, setPageObject] = useState(null);

  useEffect(() => {
    if (caseId && !pageObject) {
      fetchPageObject(caseId, 36).then((data) => {
        setPageObject(data);
      });
    }
  }, [caseId, pageObject]);

  const handleApplyFilter = (filterData) => {
    setCurrentFilter(filterData);
  };

  useEffect(() => {
    setCaseTypesWithCases(null);
  }, [currentTab, currentFilter]);

  const { allFirmCaseTypes, loading } = useCaseTypes(currentFilter);
  const caseTypeHasCases = (caseType) => {
    if (!caseTypesWithCases) return true;

    const id = caseType.id ?? caseType.case_type_id;
    const normalizedName = String(caseType.name || "")
      .trim()
      .toLowerCase();

    return (
      (id !== null && id !== undefined && caseTypesWithCases.has(`id:${id}`)) ||
      (normalizedName && caseTypesWithCases.has(`name:${normalizedName}`))
    );
  };
  const tabsData = [
    {
      id: "caseage",
      label: "Age",
      className: "tab-trapezium-cycles-corners",
      background: "var(--primary-5)",
      onClick: () => handleFilterChange("caseage"),
      leftHand: false,
    },
    {
      id: "state",
      label: "State",
      className: "tab-trapezium-cycles-corners",
      background: "var(--primary-5)",
      onClick: () => handleFilterChange("state"),
      leftHand: false,
    },

    {
      id: "county",
      label: "County",
      className: "tab-trapezium-cycles-corners",
      background: "var(--primary-5)",
      onClick: () => handleFilterChange("county"),
      leftHand: false,
    },

    {
      id: "injuries",
      label: "Injuries",
      className: "tab-trapezium-cycles-corners",
      background: "var(--primary-5)",
      onClick: () => handleFilterChange("injuries"),
      leftHand: false,
    },
  ];

  useEffect(() => {
    // sending page ids directly now.
    fetchShakespeareStatus(caseId, 36, dispatch);
  }, []);

  const getFilterTabLabel = () => {
    if (!currentFilter) {
      return "Stage / Status";
    } else if (currentFilter.type === "stage") {
      return `Showing Cases in ${currentFilter.name} Stage`;
    } else if (currentFilter.type === "status") {
      return `Showing Cases With ${currentFilter.name} Status`;
    }
    return "Stage / Status";
  };

  const getFilterTabLabel2 = () => {
    if (!currentFilter) {
      return "All Cases Regardless of Stage and Status";
    } else if (currentFilter.type === "stage") {
      return `Showing Cases in ${currentFilter.name} Stage`;
    } else if (currentFilter.type === "status") {
      return `Showing Cases With ${currentFilter.name} Status`;
    }
    return "All Cases Regardless of Stage and Status";
  };

  const filterData = [
    {
      id: "filterStatusStage",
      label: getFilterTabLabel(),
      className: "tab-trapezium-cycles-corners",
      background: "var(--primary-60)",
      activeColor: "white",
      onClick: () => {
        setShowFilterModal(true);
      },
      leftHand: true,
      span: (
        <span
          className="ic-avatar d-flex align-items-center m-r-5 ic-15"
          style={{
            width: "15px",
            height: "15px",
          }}
        >
          <img src={FilterIcon} width="25" alt="Filter" />
        </span>
      ),
    },
  ];

  const mainRef = useRef(null);
  const containerRef = useRef(null);
  const [rowCount, setRowCount] = useState(0);

  useEffect(() => {
    const calculateRows = () => {
      if (allFirmCaseTypes.length === 0) {
        setRowCount(0);
        return;
      }

      const mainRect = mainRef.current?.getBoundingClientRect();
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!mainRect || !containerRect) return;

      const availableHeight = Math.max(0, mainRect.bottom - containerRect.top);
      const fixedRows = 2 + allFirmCaseTypes.length;
      const rows = Math.max(0, Math.floor(availableHeight / 25) - fixedRows);

      setRowCount(rows);
    };

    calculateRows();

    window.addEventListener("resize", calculateRows);

    return () => {
      window.removeEventListener("resize", calculateRows);
    };
  }, [allFirmCaseTypes]);

  const [sideBarCollapsed, setSideBarCollapsed] = useState(null);

  useEffect(() => {
    const getUserPreferencesFromStorage = () => {
      const preferences = localStorage.getItem("userPreferences");
      const preferencesObj = preferences ? JSON.parse(preferences) : null;
      setSideBarCollapsed(preferencesObj?.SidebarView);
    };
    getUserPreferencesFromStorage();
  }, []);

  return (
    <>
      <div className={`page-wrapper ${sideBarCollapsed ? "force-collapsed" : ""}`}>
        <Sidebar />
        <div className="page-container overflow-hidden">
          <NavBar flaggedPageName={"36"} />
          <ActionBarComponent
            src={CaseNavigatorIcon}
            page_name={pageObject?.name}
            pageObj={pageObject}
            isChecklist={pageObject?.PageChklst || false}
          />
          <CaseNavigatorModalProvider>
            <div className="TreatmentPage-main-container invisible-scrollbar" ref={mainRef}>
              {/*  */}
              <div
                className="d-grid w-100 align-items-center"
                style={{ gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)" }}
              >
                <div className="d-flex align-items-center justify-content-start">
                  <NewChangesTabs
                    currentTab={currentTab}
                    tabsData={tabsData}
                    height={25}
                    labelClass=" justify-content-start pe-2"
                    // widthTab={true}
                    // mainContainerClass="pe-0"
                  />
                </div>
                {/* <NewTabs tabsData={tabsData} currentTab={currentTab} /> */}
                <div className="d-flex align-items-center justify-content-center">
                  <NewTabs
                    currentTab={currentTab}
                    tabsData={filterData}
                    popupTabs={true}
                    mainContainerClass="pe-0"
                    containerClass="ps-2 pe-2"
                    widthTab={true}
                  />
                </div>
                <div aria-hidden="true" />
              </div>
              {loading ? (
                <>
                  <div
                    style={{
                      width: "100%",
                      padding: "5px",
                      margin: "25px auto",
                      color: "var(--primary)",
                    }}
                    className="m-t-25"
                  >
                    <LinearProgress color="inherit" />
                  </div>
                  <div
                    style={{
                      width: "100%",
                      padding: "5px",
                      margin: "25px auto",
                      color: "var(--primary)",
                    }}
                    className="m-t-25"
                  >
                    <LinearProgress color="inherit" />
                  </div>
                  <div
                    style={{
                      width: "100%",
                      padding: "5px",
                      margin: "25px auto",
                      color: "var(--primary)",
                    }}
                    className="m-t-25"
                  >
                    <LinearProgress color="inherit" />
                  </div>
                  <div
                    style={{
                      width: "100%",
                      padding: "5px",
                      margin: "25px auto",
                      color: "var(--primary)",
                    }}
                    className="m-t-25"
                  >
                    <LinearProgress color="inherit" />
                  </div>
                  <div
                    style={{
                      width: "100%",
                      padding: "5px",
                      margin: "25px auto",
                      color: "var(--primary)",
                    }}
                    className="m-t-25"
                  >
                    <LinearProgress color="inherit" />
                  </div>
                  <div
                    style={{
                      width: "100%",
                      padding: "5px",
                      margin: "25px auto",
                      color: "var(--primary)",
                    }}
                    className="m-t-25"
                  >
                    <LinearProgress color="inherit" />
                  </div>
                  <div
                    style={{
                      width: "100%",
                      padding: "5px",
                      margin: "25px auto",
                      color: "var(--primary)",
                    }}
                    className="m-t-25"
                  >
                    <LinearProgress color="inherit" />
                  </div>
                  <div
                    style={{
                      width: "100%",
                      padding: "5px",
                      margin: "25px auto",
                      color: "var(--primary)",
                    }}
                    className="m-t-25"
                  >
                    <LinearProgress color="inherit" />
                  </div>
                </>
              ) : (
                <>
                  <div
                    className="d-flex align-items-center justify-content-center height-25"
                    style={{
                      background: "var(--primary)",
                      fontSize: "14px",
                      color: "white",
                      fontWeight: "600",
                    }}
                  >
                    {getFilterTabLabel2()}
                  </div>
                  <div className="row mx-auto" ref={containerRef}>
                    <div className="col-auto p-r-0 p-l-0" id="casetypes_block">
                      <div className="h-100">
                        <p
                          style={{
                            fontWeight: "600",
                          }}
                          className="all_case_type_heading text-primary bg-primary-10 p-l-5 p-r-5 h-25px d-flex align-items-center justify-content-center"
                        >
                          {/* Additional Header */}
                        </p>
                        <p
                          style={{
                            fontWeight: "600",
                          }}
                          className="all_case_type_heading text-primary bg-primary-4 p-l-5 p-r-5 h-25px d-flex align-items-center justify-content-center"
                        >
                          All Case Types
                        </p>
                        <ul className="item-list list-unstyled text-primary">
                          {allFirmCaseTypes.map((caseType, index) => (
                            <li
                              key={index}
                              className={`d-flex align-items-center justify-content-start p-l-5 p-r-5 h-25px ${
                                !caseTypeHasCases(caseType) ? "text-primary-50" : ""
                              }`}
                              style={{
                                fontWeight: "600",
                                color: !caseTypeHasCases(caseType) ? undefined : "black",
                                background:
                                  index % 2 === 0 ? "var(--primary-2)" : "var(--primary-4)",
                              }}
                            >
                              <span
                                className="ic-avatar d-flex align-items-center justify-content-center m-r-5"
                                style={{
                                  width: "15px",
                                  height: "15px",
                                }}
                              >
                                {caseType.casetype_icon && (
                                  <img
                                    style={{
                                      width: "15px",
                                      height: "15px",
                                    }}
                                    src={caseType.casetype_icon}
                                  />
                                )}
                              </span>
                              {caseType.name}
                            </li>
                          ))}

                          {Array.from({ length: rowCount }, (_, index) => (
                            <li
                              key={`empty-${index}`}
                              style={{
                                background:
                                  index % 2 === 0 ? "var(--primary-4)" : "var(--primary-2)",
                              }}
                              className="d-flex align-items-center empty-li h-25px"
                            />
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="col p-r-0 p-l-0">
                      <div className="">
                        {currentTab === "caseage" && (
                          <CaseAgeTab
                            caseTypes={allFirmCaseTypes}
                            currentFilter={currentFilter}
                            rowCount={rowCount + allFirmCaseTypes.length}
                            onCaseTypeAvailabilityChange={setCaseTypesWithCases}
                          />
                        )}
                        {currentTab === "state" && (
                          <StateTab
                            caseTypes={allFirmCaseTypes}
                            currentFilter={currentFilter}
                            rowCount={rowCount + allFirmCaseTypes.length}
                            onCaseTypeAvailabilityChange={setCaseTypesWithCases}
                          />
                        )}
                        {currentTab === "county" && (
                          <CountyTab
                            caseTypes={allFirmCaseTypes}
                            currentFilter={currentFilter}
                            rowCount={rowCount + allFirmCaseTypes.length}
                            onCaseTypeAvailabilityChange={setCaseTypesWithCases}
                          />
                        )}
                        {currentTab === "injuries" && (
                          <InjuryBodyPartTab
                            caseTypes={allFirmCaseTypes}
                            currentFilter={currentFilter}
                            rowCount={rowCount + allFirmCaseTypes.length}
                            onCaseTypeAvailabilityChange={setCaseTypesWithCases}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </CaseNavigatorModalProvider>
        </div>
      </div>

      <Footer />
      {showFilterModal && (
        <StageStatusFilterModal
          show={showFilterModal}
          onHide={() => setShowFilterModal(false)}
          onApplyFilter={handleApplyFilter}
          currentFilter={currentFilter}
        />
      )}
    </>
  );
};

export default NewCaseNavigatorPage;
